import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from "@mui/material";
import { Helmet } from "react-helmet";
import Card from "@components/cards/Card/Card";
import { getRootDomain } from "@util/domain";
import { deleteManagedTribute, fetchManagedTribute, ManagedTribute } from "@services/tributeManagementService";

const TributeDeletion = ({ token }: { token: string }) => {
    const [tribute, setTribute] = useState<ManagedTribute | null>(null);
    const [busy, setBusy] = useState(true);
    const [unavailable, setUnavailable] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [error, setError] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);

    const reportError = (err: unknown) => {
        if (axios.isAxiosError(err) && err.response?.status === 410) setUnavailable(true);
        setError(axios.isAxiosError<{ message?: string }>(err)
            ? err.response?.data?.message || "Unable to connect. Please try again."
            : "Something went wrong. Please try again.");
    };

    useEffect(() => {
        let active = true;
        if (!token) {
            setUnavailable(true);
            setBusy(false);
            return () => { active = false; };
        }
        fetchManagedTribute(token).then((data) => {
            if (!active) return;
            setTribute(data);
        }).catch((err) => { if (active) reportError(err); })
            .finally(() => { if (active) setBusy(false); });
        return () => { active = false; };
    }, [token]);

    const remove = async () => {
        setBusy(true);
        setError("");
        try {
            await deleteManagedTribute(token);
            setDeleted(true);
            setConfirmDelete(false);
        } catch (err) { reportError(err); } finally { setBusy(false); }
    };

    return (
        <Box sx={{ width: "100%", maxWidth: 640, mx: "auto", my: 3 }}>
            <Helmet>
                <title>Delete your tribute | Forever In Our Hearts</title>
                <meta name="robots" content="noindex,nofollow" />
                <meta name="referrer" content="no-referrer" />
            </Helmet>
            <Card>
                <Stack spacing={3}>
                    <Typography variant="h5" component="h1">Your tribute</Typography>
                    {deleted ? (
                        <>
                            <Alert severity="success">Your tribute has been deleted.</Alert>
                            <Typography>You can return to the memorial to post a new tribute.</Typography>
                            {tribute && <Button variant="contained" href={tribute.memorialUrl}>Post a new tribute</Button>}
                        </>
                    ) : unavailable ? (
                        <>
                            <Typography>This private link has expired or the tribute is no longer available. Links are valid for 30 days after posting.</Typography>
                            <Button href={`${getRootDomain()}/contact`}>Contact us for help from the page administrator</Button>
                        </>
                    ) : (
                        <>
                            <Typography>Made a mistake or want to make a change? You can delete this tribute and post a new one on the memorial page. This private link is valid for 30 days after posting. Please keep it to yourself.</Typography>
                            {error && <Alert severity="error">{error}</Alert>}
                            {busy && <Typography role="status">Please wait...</Typography>}
                            {!tribute && !busy && <Button onClick={() => window.location.reload()}>Try again</Button>}
                            {tribute && (
                                <Stack spacing={3}>
                                    <Typography variant="h6" component="h2">{tribute.name}</Typography>
                                    {tribute.message && <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{tribute.message}</Typography>}
                                    {tribute.media && (
                                        tribute.media.type === "image"
                                            ? <img src={tribute.media.src} alt="Your tribute" style={{ maxWidth: "100%", maxHeight: 320, objectFit: "contain" }} />
                                            : <video src={tribute.media.src} controls style={{ maxWidth: "100%", maxHeight: 320 }} />
                                    )}
                                    <Button color="error" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete tribute</Button>
                                </Stack>
                            )}
                        </>
                    )}
                </Stack>
            </Card>
            <Dialog open={confirmDelete && !unavailable} onClose={() => { if (!busy) setConfirmDelete(false); }}>
                <DialogTitle>Delete your tribute?</DialogTitle>
                <DialogContent>
                    This removes your message and any attached photo or video. It cannot be undone.
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={() => setConfirmDelete(false)}>Keep tribute</Button>
                    <Button color="error" disabled={busy} onClick={remove}>Delete tribute</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const ManageTributePage = () => {
    const { hash } = useLocation();
    // Remount when another email link changes only the fragment in the same tab.
    // The fragment is never sent to the web server or in HTTP referrers.
    const token = new URLSearchParams(hash.slice(1)).get("token") || "";
    return <TributeDeletion key={token} token={token} />;
};

export default ManageTributePage;
